import { useEffect, useState } from "react";
import { api, type DietCheckin as DietCheckinT } from "../../lib/api";
import { EmptyState } from "../../components/EmptyState";

const DECISION_CLS: Record<string, string> = {
  keep: "pill-positive",
  advance: "pill-warn",
  regress: "pill-warn",
};
const DECISION_LABEL: Record<string, string> = {
  keep: "Hold this sheet",
  advance: "Advance a sheet",
  regress: "Step back a sheet",
};

export default function DietCheckin() {
  const [data, setData] = useState<DietCheckinT | null | "error">(null);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);

  function load() {
    api.diet.checkin().then(setData).catch(() => setData("error"));
  }
  useEffect(load, []);

  const d = data && data !== "error" ? data : null;

  async function apply() {
    if (!d?.target_phase) return;
    setBusy(true);
    try {
      await api.diet.applyCheckin(d.target_phase.id);
      setMsg(`Active phase set to ${d.target_phase.name}.`);
      load();
    } catch {
      setMsg("Couldn't apply.");
    } finally {
      setBusy(false);
    }
  }

  if (data === "error") return <p className="muted">Couldn't load the check-in.</p>;
  if (!d) return <p className="muted">Loading…</p>;

  if (!d.has_plan) {
    return (
      <div className="animate-fade-in">
        <h2 className="section-title mb-3">Weekly check-in</h2>
        <EmptyState title="Set up your plan first">Confirm your template and active phase on the Template tab.</EmptyState>
      </div>
    );
  }

  const rateLb = d.rate_lb_week;
  const losing = (rateLb ?? 0) < 0;

  return (
    <div className="animate-fade-in">
      <h2 className="section-title mb-1">Weekly check-in</h2>
      <p className="text-ink-subtle text-small mb-4">
        Your weigh-in trend vs the RP target for {d.current_phase?.name}. Weigh in 2–3× a week, first
        thing, for the most reliable read.
      </p>

      {!d.has_enough_data ? (
        <EmptyState title="Need a bit more weigh-in data">
          A week or so of Withings weigh-ins and the rate + recommendation appears here.
        </EmptyState>
      ) : (
        <>
          <div className="grid grid-cols-3 gap-2">
            <Stat label="Weight" value={d.current_weight_kg != null ? `${d.current_weight_kg}` : "—"} unit="kg" />
            <Stat
              label="Rate / week"
              value={rateLb != null ? `${rateLb > 0 ? "+" : ""}${rateLb}` : "—"}
              unit="lb"
              accent
            />
            <Stat
              label="Target"
              value={d.band_lb ? `${d.band_lb.lo}–${d.band_lb.hi}` : "—"}
              unit={losing ? "lb loss" : "lb"}
            />
          </div>

          <div className="card card-pad mt-4">
            <div className="mb-2 flex items-center justify-between">
              <span className="field-label">Recommendation</span>
              {d.decision && <span className={DECISION_CLS[d.decision]}>{DECISION_LABEL[d.decision]}</span>}
            </div>
            <p className="text-sm text-ink-muted">{d.reason}</p>
            {d.decision !== "keep" && d.target_phase && d.target_phase.id !== d.current_phase?.id && (
              <div className="mt-4">
                <button className="btn-accent w-full py-2.5" onClick={apply} disabled={busy}>
                  {busy ? "Applying…" : `Move to ${d.target_phase.name}`}
                </button>
              </div>
            )}
            {msg && <p className="mt-2 text-sm text-ink-muted">{msg}</p>}
          </div>

          <p className="mt-3 text-xs text-ink-subtle">
            Note: the first week of a new sheet is noisy from water shifts — give a change ~2 weeks
            before judging it.
          </p>
        </>
      )}
    </div>
  );
}

function Stat({ label, value, unit, accent }: { label: string; value: string; unit: string; accent?: boolean }) {
  return (
    <div className="rounded-md border border-ink-line bg-canvas px-2 py-2 text-center">
      <div className="text-[10px] uppercase tracking-wider text-ink-subtle">{label}</div>
      <div className={`mt-0.5 font-display text-lg tabular-nums ${accent ? "text-accent" : "text-ink"}`}>{value}</div>
      <div className="text-[10px] text-ink-subtle">{unit}</div>
    </div>
  );
}
